import { useEffect, useRef, useState, FC, useCallback, memo, useMemo } from 'react';

import Countdown from 'react-countdown';
import { useForm } from 'react-hook-form';
import { FiPhoneIncoming } from 'react-icons/fi';
import { RiUser2Line } from 'react-icons/ri';
import { useLocation } from 'react-router-dom';
import Turnstile from 'react-turnstile';

import {
  RegFormUser,
  RegUser,
  Country,
  RegFormFields,
  LoginFormFields,
  OtpMedium
} from '@common/types';
import config from '@config';
import { FormController } from '@elements';
import { PhoneNumberFieldWithContext } from '@elements/adaptive-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Sentry from '@sentry/react';
import insureAPIs from '@services/apis/insure';
import { useCountryStore } from '@store/countries';
import { useIsNRIQuote, useAllowedCountriesForLogin } from '@store/utils';
import {
  Box,
  Stack,
  Text,
  Group,
  Button,
  TextInput,
  Paper,
  PinInput,
  Divider,
  Center,
  showNotification
} from '@ui';
import { COUNTRY_INDIA, SMS } from '@utils/constants';
import { useGetLoginTypeAndInternalId, usePolicyId } from '@utils/hooks';

import classes from './style.module.css';
import {
  otpValidations,
  regUserDetailsValidations,
  loginPhoneNumValidations
} from './validations';

export type RegistrationFormProps = {
  userDetails: Partial<RegUser>;
  countries?: Country[];
  enableGlobalCountryList?: boolean;
  onOtpSent?: () => void;
  isLoginForm?: boolean;
  onAuthSuccess?: (
    userDetails: RegUser & Partial<Pick<RegUser, 'fullName'>>
  ) => void;
  /**
   *
   * The callback should:
   * 1. throw if OTP verification fails.
   * 2. finalize product
   */
  verifyOTPAndFinalizeProduct?: (
    userDetails: RegUser & Partial<Pick<RegUser, 'fullName'>>
  ) => Promise<any> | PromiseRejectionEvent;
  isPolicyDocsVerificationForm?: boolean;
  onPolicyDocsVerificationSuccess?: (
    userDetails: RegUser & Partial<Pick<RegUser, 'fullName'>>
  ) => Promise<any> | PromiseRejectionEvent;
} & (
  | {
      isLoginForm: boolean;
      onAuthSuccess: (
        userDetails: RegUser & Partial<Pick<RegUser, 'fullName'>>
      ) => void;
    }
  | {
      verifyOTPAndFinalizeProduct: (
        userDetails: RegUser & Partial<Pick<RegUser, 'fullName'>>
      ) => Promise<any> | PromiseRejectionEvent;
    }
  | {
      isPolicyDocsVerificationForm: boolean;
      onPolicyDocsVerificationSuccess: (
        userDetails: RegUser & Partial<Pick<RegUser, 'fullName'>>
      ) => Promise<any> | PromiseRejectionEvent;
    }
);

const TurnstileWidget = (props: any) => {
  return <Turnstile sitekey={config.TURN_STILE_TOKEN} onVerify={props.onVerify} />;
};

const CountdownTimer = memo(
  ({
    counterKey,
    medium,
    resendingOtp,
    onResendOtp
  }: {
    counterKey: number;
    medium: OtpMedium;
    resendingOtp: boolean;
    onResendOtp: (medium: OtpMedium) => void;
  }) => {
    const countDownRenderer = useCallback(
      ({ seconds, completed }: { seconds: number; completed: boolean }) => {
        if (completed) {
          return (
            <Stack gap="sm" w="100%">
              <Button
                onClick={() => onResendOtp(medium)}
                size="sm"
                type="button"
                variant="transparent"
              >
                Resend OTP via {medium}
              </Button>
              <Divider label="Or" />
              <Button
                variant="outline"
                size="xl"
                fullWidth
                loading={resendingOtp}
                type="button"
                color={medium !== 'SMS' ? 'primary' : 'secondary'}
                c={medium !== 'SMS' ? undefined : 'success.4'}
                onClick={() => {
                  onResendOtp(medium === 'SMS' ? 'WhatsApp' : 'SMS');
                }}
                mb={{ base: 24, sm: 0 }}
              >
                {medium === 'SMS' ? 'Get OTP via WhatsApp' : 'Get OTP via SMS'}
              </Button>
            </Stack>
          );
        } else {
          return (
            <Stack gap="sm" w="100%">
              <Text fz={12} c="#8B8D98" ta="center">
                Resend OTP via {medium} in{' '}
                <Text fz={12} c="primary.4">
                  {seconds}
                </Text>
                s
              </Text>
              <Divider label="Or" />
              <Button
                variant="outline"
                size="xl"
                fullWidth
                loading={resendingOtp}
                type="button"
                color={medium !== 'SMS' ? 'primary' : 'secondary'}
                mb={{ base: 24, sm: 0 }}
                disabled
              >
                {medium === 'SMS' ? 'Get OTP via WhatsApp' : 'Get OTP via SMS'}
              </Button>
            </Stack>
          );
        }
      },
      [medium, onResendOtp, resendingOtp]
    );

    return (
      <Countdown
        key={counterKey}
        date={Date.now() + 20000}
        renderer={countDownRenderer}
      />
    );
  }
);

const RegistrationForm: FC<RegistrationFormProps> = ({
  isLoginForm = false,
  userDetails,
  countries: propCountries,
  enableGlobalCountryList,
  onAuthSuccess,
  verifyOTPAndFinalizeProduct,
  onOtpSent,
  isPolicyDocsVerificationForm,
  onPolicyDocsVerificationSuccess
}) => {
  const [otpFormVisible, showOtpForm] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [resendingOtp, setResendingOtp] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [medium, setMedium] = useState<OtpMedium>(SMS);

  const userRef = useRef<Partial<RegFormUser>>(userDetails);

  const isNRI = useIsNRIQuote();
  const location = useLocation();
  const notQuotePage = !(
    location.pathname.includes('quote') || location.pathname.includes('fq')
  );

  const allowedCountries = useAllowedCountriesForLogin();

  const { type, internal_id } = useGetLoginTypeAndInternalId();

  const [counterKey, setCounterKey] = useState<number>(1);

  const { countries, loadingCountries, fetchCountries } = useCountryStore(
    (state) => ({
      countries: state.countries,
      loadingCountries: state.isLoading,
      fetchCountries: state.fetchCountries
    })
  );

  useEffect(() => {
    if (enableGlobalCountryList) {
      fetchCountries();
    }
  }, [enableGlobalCountryList, fetchCountries]);

  // Use propCountries if provided, otherwise use store countries
  const resolvedCountries = useMemo(() => {
    if (propCountries && propCountries.length > 0) return propCountries;
    if (countries && countries.length > 0) return countries;
    return [COUNTRY_INDIA];
  }, [propCountries, countries]);

  const policyId = usePolicyId();

  const validationSchema = isLoginForm
    ? loginPhoneNumValidations()
    : regUserDetailsValidations();

  const defaultCountry = useMemo(() => {
    // If it's a policy docs verification form, user can only verify docs using the country code and mobile in the policy
    if (isPolicyDocsVerificationForm) {
      const allowedCountry = resolvedCountries.find(
        (country) => country.calling_code === userDetails?.country_code
      );

      return allowedCountry;
    }

    if (Array.isArray(allowedCountries) && allowedCountries.length > 0) {
      const countryFromCountryCode = resolvedCountries.find(
        (country) => country.name === allowedCountries[0]
      );

      if (countryFromCountryCode) {
        return countryFromCountryCode;
      }
    }

    return COUNTRY_INDIA;
  }, [
    allowedCountries,
    resolvedCountries,
    userDetails,
    isPolicyDocsVerificationForm
  ]);

  // User form with react-hook-form
  const userForm = useForm<RegFormFields | LoginFormFields>({
    defaultValues: {
      ...(userDetails.fullName && { fullName: userDetails.fullName }),
      mobile: userDetails.mobile || '',
      country: defaultCountry
    } as RegFormFields | LoginFormFields,
    resolver: isPolicyDocsVerificationForm
      ? undefined
      : (yupResolver(validationSchema) as any)
  });

  // OTP form with react-hook-form
  const otpForm = useForm({
    defaultValues: { otp: '' },
    resolver: yupResolver(otpValidations())
  });

  const switchToOtpForm = () => {
    showOtpForm(true);
  };

  const sendOtp = useCallback(
    async (values?: RegFormFields | LoginFormFields, medium: OtpMedium = SMS) => {
      setResendingOtp(true);
      setMedium(medium);

      try {
        if (values?.mobile && values?.country) {
          userRef.current = {
            ...values,
            mobile: values.mobile,
            country: values.country
          };
        }

        if (!userRef.current?.mobile || !userRef.current?.country?.calling_code)
          throw new Error('No phone number');

        const sendOTP =
          medium === 'SMS' ? insureAPIs.sendOTP : insureAPIs.sendWhatsAppOTP;

        const payload = {
          phone_num: userRef.current ? userRef.current?.mobile : '',
          country_code: userRef.current?.country.calling_code,
          turnstile_token: turnstileToken,
          type,
          internal_id,
          isPolicyDocsVerificationForm
        };

        await sendOTP(payload);

        showNotification({
          message: medium === 'SMS' ? 'OTP sent via SMS' : 'OTP sent via WhatsApp',
          title: 'OTP Sent',
          color: 'success'
        });
        switchToOtpForm();
        setCounterKey((prev) => prev + 1);
        onOtpSent && onOtpSent();
      } catch (error: any) {
        if (!error.notified) {
          showNotification({
            title: 'OTP not sent, Please try again!',
            message: `An error occurred while generating OTP, please try again. Make sure
                 you are connected to the internet.`,
            color: 'danger'
          });
        }
      } finally {
        setResendingOtp(false);
      }
    },
    [turnstileToken, type, internal_id, isPolicyDocsVerificationForm, onOtpSent]
  );

  const submitOtp = useCallback(
    async ({ otp }: any) => {
      setSending(true);
      try {
        if (!userRef.current?.mobile || !userRef.current?.country)
          throw new Error('Cannot verify the OTP');

        // If it's a login form
        if (isLoginForm) {
          const POLICY_ID =
            policyId || window.location.pathname.split('/').pop() || '';

          // Verify OTP for login
          await insureAPIs.checkUserAuth(POLICY_ID, {
            phone_num: userRef.current?.mobile,
            otp,
            country_code: userRef.current?.country.calling_code
          });

          onAuthSuccess &&
            onAuthSuccess({
              fullName: userRef.current?.fullName,
              otp,
              mobile: userRef.current?.mobile,
              country_code: userRef.current.country.calling_code
            });

          showNotification({
            message: ' You have successfully logged in with your mobile number',
            title: 'Login successful',
            color: 'success'
          });
          setSending(false);
        } else if (isPolicyDocsVerificationForm) {
          onPolicyDocsVerificationSuccess &&
            (await onPolicyDocsVerificationSuccess({
              otp,
              mobile: userRef.current?.mobile,
              country_code: userRef.current?.country.calling_code
            }));
          showNotification({
            message: 'You have successfully verified your policy documents',
            title: 'Verification successful',
            color: 'success'
          });
        } else {
          // If it's a registration form
          const success =
            verifyOTPAndFinalizeProduct &&
            (await verifyOTPAndFinalizeProduct({
              fullName: userRef.current?.fullName,
              otp,
              mobile: userRef.current?.mobile,
              country_code: userRef.current?.country.calling_code
            }));
          if (success)
            showNotification({
              message: 'You have successfully verified your mobile number',
              title: 'Authentication successful',
              color: 'success'
            });
        }
        setSending(false);
      } catch (error: any) {
        Sentry.captureException(error);
        setSending(false);
      }
    },
    [
      isLoginForm,
      policyId,
      onAuthSuccess,
      isPolicyDocsVerificationForm,
      onPolicyDocsVerificationSuccess,
      verifyOTPAndFinalizeProduct
    ]
  );

  const handleResendOtp = useCallback(
    (medium: OtpMedium) => {
      setCounterKey((prev) => prev + 1);
      sendOtp(undefined, medium);
    },
    [sendOtp]
  );

  return (
    <>
      <Box
        display={!otpFormVisible ? 'block' : 'none'}
        pt={{ base: 8, sm: 26 }}
        p={{ base: 16, sm: 40 }}
      >
        <Box
          component="form"
          onSubmit={userForm.handleSubmit((values) => sendOtp(values, 'SMS'))}
        >
          <Stack gap={24}>
            <Stack gap={12}>
              <Text fz={{ base: 24, sm: 28 }} fw={600} size="lg" lh={1.1}>
                {isPolicyDocsVerificationForm
                  ? 'Verify your policy documents'
                  : 'Verify your phone number'}
              </Text>
              <Text size="md" c="secondary" fz={{ base: 13, sm: 14 }}>
                {isLoginForm
                  ? "Enter the Proposer's phone number"
                  : isPolicyDocsVerificationForm
                  ? 'Verify your policy documents through OTP sent to your registered mobile number'
                  : 'We send the application link and policy details on your phone number. The phone number must belong to the proposer.'}
              </Text>
            </Stack>

            {!isLoginForm && !isPolicyDocsVerificationForm && (
              <FormController name="fullName" control={userForm.control}>
                <TextInput placeholder="John Doe" label="Full Name" size="xl" />
              </FormController>
            )}

            <PhoneNumberFieldWithContext
              mobileFieldName="mobile"
              countryFieldName="country"
              label="Mobile Number"
              placeholder="Mobile Number"
              disabled={isPolicyDocsVerificationForm}
              disableCountryCode={!isNRI && !notQuotePage}
              size="xl"
              loading={loadingCountries}
              countries={resolvedCountries}
              control={userForm.control as any}
            />
            <Center>
              <TurnstileWidget
                onVerify={(token: string) => {
                  setTurnstileToken(token);
                }}
              />
            </Center>
            <Stack align="center">
              <Button
                id="sign-in-button"
                variant="filled"
                size="xl"
                fullWidth
                disabled={!userForm.formState.isValid || resendingOtp}
                loading={resendingOtp && medium === 'SMS'}
                type="submit"
              >
                Get OTP via SMS
              </Button>
              <Button
                variant="outline"
                size="xl"
                fullWidth
                disabled={!userForm.formState.isValid || resendingOtp}
                loading={resendingOtp && medium === 'WhatsApp'}
                type="button"
                color="secondary"
                c={
                  !(!userForm.formState.isValid || resendingOtp)
                    ? 'success.4'
                    : undefined
                }
                onClick={() => {
                  userForm.handleSubmit((values) => sendOtp(values, 'WhatsApp'))();
                }}
              >
                Get OTP via WhatsApp
              </Button>
              <Text size="xs" c="dimmed" ta="center">
                {`Don't worry, we will never spam you`}
              </Text>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <Box
        display={otpFormVisible ? 'block' : 'none'}
        pt={{ base: 8, sm: 26 }}
        p={{ base: 16, sm: 40 }}
      >
        <Box
          component="form"
          onSubmit={otpForm.handleSubmit(submitOtp)}
          autoComplete="off"
        >
          <Stack gap={24}>
            <Stack gap={12}>
              <Text fw={600} fz={28} lh={1.2}>
                {isPolicyDocsVerificationForm
                  ? 'Verify your policy documents'
                  : 'Enter your OTP'}
              </Text>
              {!isLoginForm && !isPolicyDocsVerificationForm && (
                <Text size="md" c="secondary" lh={1.5}>
                  Your OTP has been successfully sent via SMS.
                </Text>
              )}
              <Paper p={12} className={classes.userInfoPaper}>
                <Stack gap="md">
                  {!isLoginForm && (
                    <Group gap="xs" className={classes.userInfoText}>
                      <RiUser2Line />
                      <Text fw={500} lh={1}>
                        {userRef.current.fullName}
                      </Text>
                    </Group>
                  )}
                  <Group justify="space-between">
                    <Group gap="xs" className={classes.userInfoText}>
                      <FiPhoneIncoming />
                      <Text fw={500} lh={1}>
                        +{userRef.current.mobile}
                      </Text>
                    </Group>
                    {!isPolicyDocsVerificationForm && (
                      <Button
                        onClick={() => {
                          otpForm.setValue('otp', '');
                          showOtpForm(false);
                        }}
                        size="sm"
                        type="button"
                        variant="transparent"
                        color="secondary"
                        style={{
                          textDecoration: 'underline'
                        }}
                      >
                        Edit Details
                      </Button>
                    )}
                  </Group>
                </Stack>
              </Paper>
            </Stack>

            <FormController name="otp" control={otpForm.control}>
              <PinInput
                inputMode="numeric"
                size="xl"
                length={6}
                placeholder="_"
                mask
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && otpForm.formState.isValid) {
                    event.preventDefault();
                    otpForm.handleSubmit(submitOtp)();
                  }
                }}
              />
            </FormController>

            <Stack align="center" gap="md">
              <Button
                id="otp-button"
                variant="filled"
                size="xl"
                fullWidth
                disabled={!otpForm.formState.isValid}
                loading={sending}
                type="submit"
              >
                Submit
              </Button>
            </Stack>

            <Group justify="center" w="100%">
              <CountdownTimer
                counterKey={counterKey}
                medium={medium}
                resendingOtp={resendingOtp}
                onResendOtp={handleResendOtp}
              />
            </Group>
          </Stack>
        </Box>
      </Box>
    </>
  );
};

export { RegistrationForm };
